import React from 'react';
import { Button } from '../ui/button';
import {
  ArrowLeft,
  Save,
  RefreshCw,
  CheckCircle2,
  Cloud,
  CloudOff,
  Loader2,
} from 'lucide-react';
import { InviteManagerDialog } from './InviteManagerDialog';

const SAVE_STATUS_STYLES = {
  saved:   { bg: '#ecfdf5', fg: '#047857' },
  saving:  { bg: '#eff6ff', fg: '#1d4ed8' },
  unsaved: { bg: '#fffbeb', fg: '#a16207' },
  error:   { bg: '#fef2f2', fg: '#b91c1c' },
  idle:    { bg: '#f1f5f9', fg: '#475569' },
};

const AutosaveIndicator = ({ saveStatus }) => {
  const { bg, fg } = SAVE_STATUS_STYLES[saveStatus] || SAVE_STATUS_STYLES.idle;
  return (
    <div
      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md min-w-[110px] justify-center"
      data-testid="autosave-status"
      style={{ background: bg, color: fg }}
    >
      {saveStatus === 'saving' && (<><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>)}
      {saveStatus === 'saved' && (<><Cloud className="w-3 h-3" /> All changes saved</>)}
      {saveStatus === 'unsaved' && (<><Cloud className="w-3 h-3" /> Unsaved changes</>)}
      {saveStatus === 'error' && (<><CloudOff className="w-3 h-3" /> Autosave failed</>)}
      {saveStatus === 'idle' && (<><Cloud className="w-3 h-3" /> Autosave on</>)}
    </div>
  );
};

export const TacticalEditorHeader = ({
  monthName,
  year,
  isOwner,
  hasUnacknowledgedChanges,
  onBack,
  onAcknowledgeChanges,
  onRollover,
  showInviteDialog,
  setShowInviteDialog,
  inviteEmail,
  setInviteEmail,
  onInviteManager,
  inviting,
  saveStatus,
  onSave,
  saving,
}) => (
  <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
    <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-lg font-semibold">
            {monthName} {year} Tactical
          </h1>
          {!isOwner && (
            <p className="text-sm text-muted-foreground">Viewing as manager</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isOwner && hasUnacknowledgedChanges && (
          <Button
            variant="outline"
            size="sm"
            onClick={onAcknowledgeChanges}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid="acknowledge-changes-button"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Acknowledge Changes
          </Button>
        )}
        {isOwner && (
          <>
            <InviteManagerDialog
              open={showInviteDialog}
              onOpenChange={setShowInviteDialog}
              inviteEmail={inviteEmail}
              onInviteEmailChange={setInviteEmail}
              onSend={onInviteManager}
              sending={inviting}
            />
            <Button variant="outline" size="sm" onClick={onRollover} data-testid="rollover-button">
              <RefreshCw className="w-4 h-4 mr-2" />
              Rollover to Next Month
            </Button>
          </>
        )}

        {isOwner && <AutosaveIndicator saveStatus={saveStatus} />}

        <Button onClick={onSave} disabled={saving} data-testid="save-button">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  </header>
);
